import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormInput,
  FormPrice,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect, useMemo } from "react";
import { z } from "zod";
import {
  DOCUMENT_CURRENCY_SYMBOL,
  formatDocumentMoney,
} from "@/shared/lib/performance-document-currency";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import type { EventStaffDTO } from "../../domain/contracts";
import type { CreateStaffPayoutDTO } from "../../domain/contracts";
import { useCreateStaffPayout } from "../api/queries";

const LAK_SYMBOL = DOCUMENT_CURRENCY_SYMBOL.LAK;

const CreateStaffPayoutFormSchema = z.object({
  items: z
    .array(
      z.object({
        eventStaffId: z.string(),
        selected: z.boolean(),
        netAmount: z.number().int().min(0),
        description: z.string().optional(),
      }),
    )
    .refine((items) => items.some((item) => item.selected && item.netAmount > 0), {
      message: "ເລືອກຢ່າງນ້ອຍ 1 Staff ແລະ ປ້ອນຍອດ",
      path: ["items"],
    }),
});

type CreateStaffPayoutFormValues = z.infer<typeof CreateStaffPayoutFormSchema>;

type CreateStaffPayoutDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  staff: EventStaffDTO[];
};

export function CreateStaffPayoutDialog({
  open,
  onOpenChange,
  eventId,
  staff,
}: CreateStaffPayoutDialogProps) {
  const createStaffPayout = useCreateStaffPayout(eventId);
  const activeStaff = useMemo(
    () => staff.filter((row) => row.isActive),
    [staff],
  );

  const methods = RHF.useForm<CreateStaffPayoutFormValues>({
    resolver: zodResolver(CreateStaffPayoutFormSchema),
    defaultValues: { items: [] },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({
      items: activeStaff.map((row) => ({
        eventStaffId: row.id,
        selected: false,
        netAmount: 0,
        description: row.roleLabel ?? row.defaultRoleLabel ?? "",
      })),
    });
  }, [open, activeStaff, methods]);

  const items = methods.watch("items") ?? [];
  const total = items
    .filter((item) => item.selected)
    .reduce((sum, item) => sum + (item.netAmount || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>ສ້າງໃບເບີກຈ່າຍ Staff</DialogTitle>
        </DialogHeader>
        <FormRoot
          methods={methods}
          onSubmit={methods.handleSubmit(async (values) => {
            const payload: CreateStaffPayoutDTO = {
              staffLineItems: values.items
                .filter((item) => item.selected && item.netAmount > 0)
                .map((item) => ({
                  eventStaffId: item.eventStaffId,
                  netAmount: item.netAmount,
                  description: item.description?.trim() || undefined,
                })),
            };
            await createStaffPayout.mutateAsync(payload);
            onOpenChange(false);
          })}
        >
          <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
            {activeStaff.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີ Staff — ເພີ່ມໃນສ່ວນທີມ Staff ກ່ອນ
              </p>
            ) : (
              activeStaff.map((row, index) => {
                const staffMeta = staff.find((item) => item.id === row.id);
                const roleLabel = row.roleLabel ?? row.defaultRoleLabel;
                return (
                  <div key={row.id} className="space-y-2">
                    <FormCheckboxCard
                      name={`items.${index}.selected`}
                      label={row.displayName}
                      description={roleLabel ?? undefined}
                    />
                    {items[index]?.selected ? (
                      <div className="ml-1 space-y-2 border-primary/20 border-l-2 pl-3">
                        <FormPrice
                          name={`items.${index}.netAmount`}
                          label="ຍອດ"
                          currencySymbol={LAK_SYMBOL}
                        />
                        <FormInput
                          name={`items.${index}.description`}
                          label="ຄຳອະທິບາຍ"
                          placeholder={
                            staffMeta?.roleLabel ??
                            staffMeta?.defaultRoleLabel ??
                            "ຄ່າຈ້າງ"
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-4 flex items-center justify-between border-t pt-3 text-sm">
            <span className="text-muted-foreground">ຍອດລວມ</span>
            <span className="font-semibold tabular-nums">
              {formatDocumentMoney(total, "LAK")}
            </span>
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={
                createStaffPayout.isPending || activeStaff.length === 0
              }
            >
              ສ້າງໃບເບີກຈ່າຍ
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
